import { useState, type ReactNode } from 'react';
import {
  FileEdit, FileCheck, FileSpreadsheet, FileText, Wallet, Lock, Ban,
  CheckCircle2, Loader2, ArrowUpRight, CalendarDays,
  ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { Transaction } from '../../types/dashboard';

interface TransactionTableProps {
  loading: boolean;
  displayedTransactions: Transaction[];
  error: string;
  calculateNetFees: (transaction: Transaction) => number;
  onViewTransaction: (transaction: Transaction) => void;
  onEditTransaction: (transaction: Transaction) => void;
  onCancelTransaction: (transaction: Transaction) => void;
  onCompromiseTransaction: (transaction: Transaction) => void;
  onRegulTransaction: (transaction: Transaction) => void;
  onActeTransaction: (transaction: Transaction) => void;
  onEncaissementTransaction: (transaction: Transaction) => void;
  onClotureTransaction: (transaction: Transaction) => void;
  onViewRegulTransaction: (transaction: Transaction) => void;
}

const ROLE_CONFIG: Record<string, { label: string; pill: string; tag: string }> = {
  vendeur:           { label: 'V',   pill: 'bg-blue-50    border-blue-100    text-blue-700',    tag: 'text-blue-400'    },
  apporteur:         { label: 'A',   pill: 'bg-emerald-50 border-emerald-100 text-emerald-700', tag: 'text-emerald-400' },
  vendeur_apporteur: { label: 'V/A', pill: 'bg-violet-50  border-violet-100  text-violet-700',  tag: 'text-violet-400'  },
  reco:              { label: 'R',   pill: 'bg-amber-50   border-amber-100   text-amber-700',   tag: 'text-amber-400'   },
  autre:             { label: 'X',   pill: 'bg-slate-100  border-slate-200   text-slate-600',   tag: 'text-slate-400'   },
};

const STATUS_CONFIG: Record<string, { label: string; dot: string; text: string; bg: string; border: string }> = {
  potentiel: { label: 'Potentiel', dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  compromis: { label: 'Compromis', dot: 'bg-blue-400',    text: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200'  },
  acte:      { label: 'Acté',      dot: 'bg-violet-400',  text: 'text-violet-700',  bg: 'bg-violet-50',  border: 'border-violet-200'},
  encaisse:  { label: 'Encaissé',  dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200'},
  cloture:   { label: 'Clôturé',   dot: 'bg-slate-400',   text: 'text-slate-500',   bg: 'bg-slate-100',  border: 'border-slate-200' },
  annule:    { label: 'Annulé',    dot: 'bg-red-400',     text: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200'   },
};

export default function TransactionTable({
  loading,
  displayedTransactions,
  error: _error,
  calculateNetFees,
  onViewTransaction,
  onEditTransaction,
  onCancelTransaction,
  onCompromiseTransaction,
  onRegulTransaction,
  onActeTransaction,
  onEncaissementTransaction,
  onClotureTransaction,
  onViewRegulTransaction,
}: TransactionTableProps) {

  const fmtAmount = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  const fmtDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  /* ── sort state ── */
  type SortKey = 'date' | 'price' | 'fees' | 'status' | 'approved' | 'agency';
  type SortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const STATUS_ORDER: Record<string, number> = {
    potentiel: 0, compromis: 1, acte: 2, encaisse: 3, cloture: 4, annule: 5,
  };

  const sorted = [...displayedTransactions].sort((a, b) => {
    if (!sortKey) return 0;
    let cmp = 0;
    switch (sortKey) {
      case 'date':     cmp = (a.compromise_date ?? '').localeCompare(b.compromise_date ?? ''); break;
      case 'price':    cmp = (a.sale_price ?? 0) - (b.sale_price ?? 0); break;
      case 'fees':     cmp = calculateNetFees(a) - calculateNetFees(b); break;
      case 'status':   cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99); break;
      case 'approved': cmp = (a.approved ? 1 : 0) - (b.approved ? 1 : 0); break;
      case 'agency':   cmp = (a.agencies[0] ?? '').localeCompare(b.agencies[0] ?? ''); break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  /* ── header cell ── */
  const SortableTH = ({ children, sk, center }: { children: ReactNode; sk?: SortKey; center?: boolean }) => {
    const active = sortKey === sk;
    return (
      <th
        onClick={sk ? () => handleSort(sk) : undefined}
        className={`px-5 py-3.5 text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap
          ${center ? 'text-center' : 'text-left'}
          ${sk ? 'cursor-pointer select-none hover:text-slate-600 transition-colors' : ''}
          ${active ? 'text-indigo-500' : 'text-slate-400'}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          {sk && (active
            ? sortDir === 'asc'
              ? <ChevronUp size={12} className="text-indigo-500" />
              : <ChevronDown size={12} className="text-indigo-500" />
            : <ChevronsUpDown size={12} className="text-slate-300" />
          )}
        </span>
      </th>
    );
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">

          {/* ── Headers ── */}
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <SortableTH sk="approved" center>Validé</SortableTH>
              <SortableTH sk="date">Date compromis</SortableTH>
              <SortableTH sk="agency">Agence</SortableTH>
              <SortableTH>Négociateur</SortableTH>
              <SortableTH>Parties</SortableTH>
              <SortableTH sk="price">Prix de vente</SortableTH>
              <SortableTH sk="fees">Honoraires</SortableTH>
              <SortableTH sk="status">Statut</SortableTH>
              <SortableTH center>Régul</SortableTH>
              <SortableTH>Actions</SortableTH>
            </tr>
          </thead>

          {/* ── Body ── */}
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-300">
                    <Loader2 size={22} className="animate-spin" />
                    <span className="text-sm">Chargement…</span>
                  </div>
                </td>
              </tr>
            ) : displayedTransactions.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-5 py-16 text-center text-sm text-slate-400">
                  Aucune transaction trouvée
                </td>
              </tr>
            ) : (
              sorted.map((t, i) => {
                const status  = STATUS_CONFIG[t.status] ?? { label: t.status, dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' };
                const netFees = calculateNetFees(t);
                const dateStr = fmtDate(t.compromise_date);
                const isLast  = i === sorted.length - 1;

                return (
                  <tr
                    key={t.id}
                    onClick={() => onViewTransaction(t)}
                    className={`cursor-pointer transition-colors hover:bg-slate-50/70 ${t.regul ? 'bg-rose-50/40 hover:bg-rose-50/70' : ''} ${!isLast ? 'border-b border-slate-100' : ''}`}
                  >

                    {/* ── Validé ── */}
                    <td className="px-5 py-4 text-center w-16">
                      {t.approved ? (
                        <span
                          title={`Validé par ${t.approved_by_name ?? ''} le ${fmtDate(t.approved_at) ?? ''}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs font-semibold"
                        >
                          <CheckCircle2 size={11} />
                          OK
                        </span>
                      ) : (
                        <span className="text-slate-200 text-sm select-none">—</span>
                      )}
                    </td>

                    {/* ── Date compromis ── */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      {dateStr ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-medium">
                          <CalendarDays size={11} className="shrink-0" />
                          {dateStr}
                        </span>
                      ) : (
                        <span className="text-slate-200 text-sm">—</span>
                      )}
                    </td>

                    {/* ── Agence ── */}
                    <td className="px-5 py-4 min-w-[110px]">
                      <div className="flex flex-col gap-1">
                        {t.agencies.length === 0
                          ? <span className="text-slate-300 text-sm">—</span>
                          : t.agencies.map((a, idx) => (
                              <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold w-fit">
                                {a}
                              </span>
                            ))
                        }
                      </div>
                    </td>

                    {/* ── Négociateur ── */}
                    <td className="px-5 py-4 min-w-[140px] max-w-[200px]">
                      <div className="flex flex-col gap-1">
                        {t.negotiators.length === 0
                          ? <span className="text-slate-300 text-sm">—</span>
                          : t.negotiators.map((n, idx) => {
                              const rc = ROLE_CONFIG[n.role] ?? ROLE_CONFIG['autre'];
                              return (
                                <span key={idx} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium w-fit ${rc.pill}`}>
                                  {n.name}
                                  <span className={`font-semibold ${rc.tag}`}>{rc.label} {n.percentage}%</span>
                                </span>
                              );
                            })
                        }
                      </div>
                    </td>

                    {/* ── Parties ── */}
                    <td className="px-5 py-4 min-w-[150px] max-w-[210px]">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-baseline gap-1.5">
                          <span className="shrink-0 text-[10px] font-bold text-amber-500 uppercase tracking-wide">Vend.</span>
                          <span className="text-sm text-slate-700 leading-tight break-words">{t.seller || '—'}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="shrink-0 text-[10px] font-bold text-sky-500 uppercase tracking-wide">Acq.</span>
                          <span className="text-sm text-slate-700 leading-tight break-words">{t.buyer || '—'}</span>
                        </div>
                      </div>
                    </td>

                    {/* ── Prix de vente ── */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-800 text-sm font-semibold">
                        {fmtAmount(t.sale_price)}
                      </span>
                    </td>

                    {/* ── Honoraires ── */}
                    <td className="px-5 py-4 min-w-[165px] whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {/* Net — badge principal */}
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-bold w-fit">
                          <ArrowUpRight size={13} className="shrink-0" />
                          {fmtAmount(netFees)}
                        </span>
                        {/* TTC / HT en secondaire */}
                        <div className="flex flex-col gap-0.5 pl-0.5">
                          <span className="text-[11px] text-slate-400">
                            <span className="font-medium text-slate-500">TTC</span> {fmtAmount(t.fees_with_tax)}
                          </span>
                          <span className="text-[11px] text-slate-400">
                            <span className="font-medium text-slate-500">HT</span> {fmtAmount(t.fees_without_tax)}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* ── Statut ── */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${status.bg} ${status.text} ${status.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${status.dot}`} />
                        {status.label}
                      </span>
                    </td>

                    {/* ── Régul ── */}
                    <td className="px-5 py-4 text-center whitespace-nowrap">
                      {t.regul && (
                        <button
                          title={t.commentaire_regul || 'Régularisation en cours'}
                          onClick={e => { e.stopPropagation(); onViewRegulTransaction(t); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-100 transition-colors"
                        >
                          <FileSpreadsheet size={11} />
                          Régul
                        </button>
                      )}
                    </td>

                    {/* ── Actions ── */}
                    <td className="px-5 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <ActionBtn onClick={() => onEditTransaction(t)}   title="Modifier"               color="indigo"><FileEdit size={13} /></ActionBtn>
                        {t.status !== 'cloture' &&
                          <ActionBtn onClick={() => onCancelTransaction(t)}   title="Annuler"            color="red">  <Ban size={13} /></ActionBtn>}
                        {t.status === 'potentiel' &&
                          <ActionBtn onClick={() => onCompromiseTransaction(t)} title="Passer en compromis" color="amber"><FileCheck size={13} /></ActionBtn>}
                        {t.status === 'compromis' && !t.regul &&
                          <ActionBtn onClick={() => onRegulTransaction(t)}  title="Demande de régul."  color="violet"><FileSpreadsheet size={13} /></ActionBtn>}
                        {t.status === 'compromis' &&
                          <ActionBtn onClick={() => onActeTransaction(t)}   title="Passer en acte"     color="blue">  <FileText size={13} /></ActionBtn>}
                        {t.status === 'acte' &&
                          <ActionBtn onClick={() => onEncaissementTransaction(t)} title="Passer en encaissé" color="emerald"><Wallet size={13} /></ActionBtn>}
                        {t.status === 'encaisse' &&
                          <ActionBtn onClick={() => onClotureTransaction(t)} title="Clôturer"          color="slate"> <Lock size={13} /></ActionBtn>}
                      </div>
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Petit bouton action réutilisable ── */
const COLOR_MAP: Record<string, string> = {
  indigo: 'bg-indigo-50  text-indigo-500  hover:bg-indigo-100',
  red:    'bg-red-50     text-red-400     hover:bg-red-100',
  amber:  'bg-amber-50   text-amber-500   hover:bg-amber-100',
  violet: 'bg-violet-50  text-violet-500  hover:bg-violet-100',
  blue:   'bg-blue-50    text-blue-500    hover:bg-blue-100',
  emerald:'bg-emerald-50 text-emerald-500 hover:bg-emerald-100',
  slate:  'bg-slate-100  text-slate-500   hover:bg-slate-200',
};

function ActionBtn({ children, onClick, title, color }: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  color: keyof typeof COLOR_MAP;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded-lg transition-colors ${COLOR_MAP[color]}`}
    >
      {children}
    </button>
  );
}
